// Probabilidade (§14.6), com P-03 (diagnostics opcionais), P-04 (catálogo fechado) e P-05 (governador), 09/10.
import * as z from 'zod/mini';
import {
  type CandidateNumber,
  CandidateNumberSchema,
  type FeedHeader,
  feedHeaderShape,
  IntSchema,
  type Iso,
  IsoSchema,
  type PlaceCode,
  PlaceCodeSchema,
  type RegionCode,
  RegionCodeSchema,
  type UfCode,
  UfCodeSchema,
} from './base';

export type ProbabilityStatus = 'off' | 'waiting_data' | 'active' | 'paused' | 'ended' | 'error';
export type ChanceBand = 'open_race' | 'favorite' | 'very_likely' | 'above_99';
/** Fração dos válidos. */
export interface Quantiles {
  q025: number;
  q10: number;
  q25?: number;
  q50: number;
  q75?: number;
  q90: number;
  q975: number;
}
/** Faixa de votos final de um candidato (P-35): quantis em votos, inteiros, q10 ≤ q50 ≤ q90. */
export interface VoteQuantiles {
  q10: number;
  q50: number;
  q90: number;
}
export interface ModelMethod {
  id: string;
  version: string;
  short_description: string;
  methodology_url: string;
  params_hash: string;
}

/** Vai DENTRO de snapshot.json (atômico com o oficial). */
export interface ProbabilityPayload {
  status: ProbabilityStatus;
  /** ≤ 120 caracteres; só valores do catálogo PUBLIC_REASONS (P-04). */
  public_reason: string | null;
  base_seq: number;
  generated_at: Iso;
  method: Pick<ModelMethod, 'id' | 'version' | 'methodology_url'>;
  /** Só com status 'active'; soma 1. */
  win_prob: Record<CandidateNumber, number> | null;
  /** Só com status 'active'. */
  final_share: Record<CandidateNumber, Quantiles> | null;
  /** P-35: faixa de votos final de cada candidato; só com status 'active' (opcional: o motor publica a partir de 19/10). */
  final_votes?: Record<CandidateNumber, VoteQuantiles> | null;
  band: ChanceBand | null;
  favorite: CandidateNumber | null;
  call: { state: 'none' | 'above_99'; candidate?: CandidateNumber; approved_at?: Iso } | null;
}

export interface PlaceProjection {
  win_prob: Record<CandidateNumber, number>;
  final_share: Record<CandidateNumber, Quantiles>;
  /** P-35: faixa de votos final de cada candidato (também por UF de governador). */
  final_votes?: Record<CandidateNumber, VoteQuantiles>;
  /** b − a, em pontos. */
  final_margin_pp?: Quantiles;
  /** Válidos ainda por apurar. */
  remaining_valid?: Quantiles;
  histogram?: {
    variable: 'final_share_b_pp';
    start: number;
    step: number;
    counts: number[];
    below: number;
    above: number;
    total: number;
  };
}

/** Arquivo completo, imutável: {feed}/p/{base_seq}/probability.json */
export interface ProbabilityDetail extends FeedHeader<'results.probability'> {
  base_seq: number;
  generated_at: Iso;
  status: ProbabilityStatus;
  method: ModelMethod;
  simulations: number;
  national: PlaceProjection;
  places: Partial<Record<PlaceCode, PlaceProjection>>;
  regions?: Partial<Record<RegionCode, PlaceProjection>>;
  /** Projeção de governador, só as UFs de 2º turno, nunca 'ZZ'; sem above_99 (P-05). */
  governor?: Partial<Record<UfCode, PlaceProjection>>;
  /** σ e γ dobrados, t com 3 graus de liberdade. */
  stress?: { win_prob: Record<CandidateNumber, number> };
  diagnostics: {
    prior_weight: number;
    places_with_data: number;
    sections_fraction: number;
    tse_lag_s: number;
    duration_ms: number;
    /** P-03 (opcionais). */
    composition?: 'none' | 'order_1t' | 'sections_list';
    regions_with_data?: number;
    units_with_data?: number;
    stress_spec?: string;
  };
}

/**
 * Catálogo fechado de `public_reason` (P-04). O texto exibido é sempre o da interface (§13.6); o motor nunca envia
 * texto livre. Valor fora do catálogo descarta o bloco (I-14).
 */
export const PUBLIC_REASONS = [
  'A projeção começa às 17h00.',
  'A projeção começa com 5% das seções apuradas.',
  'Projeção aguardando novos números do TSE.',
  'Projeção pausada.',
  'Projeção encerrada.',
  // K05 (§8 de 14/10, noite; texto do §8 de 15/10, manhã): o mínimo novo, bem menor; o de 5% fica aceito até o motor
  // trocar. No fim da lista: o simulador lê os motivos pela posição.
  'A projeção começa com as primeiras seções apuradas.',
  // o início medido pelo especialista (§8 de 15/10, tarde, D): aceito junto dos outros, para a ordem dos merges não
  // derrubar o quadro da chance
  'A projeção começa com 1% das seções apuradas, com dados de pelo menos 14 unidades da federação e das cinco regiões.',
] as const;
/** `reason` dos eventos model_paused, model_resumed e model_ended (P-04); `null` também vale. */
export const MODEL_EVENT_REASONS = ['fonte_sem_novidade', 'saude_do_modelo', 'decisao_editorial'] as const;

export const ProbabilityStatusSchema = z.enum(['off', 'waiting_data', 'active', 'paused', 'ended', 'error']);
export const ChanceBandSchema = z.enum(['open_race', 'favorite', 'very_likely', 'above_99']);
export const QuantilesSchema = z.object({
  q025: z.number(),
  q10: z.number(),
  q25: z.optional(z.number()),
  q50: z.number(),
  q75: z.optional(z.number()),
  q90: z.number(),
  q975: z.number(),
});
export const VoteQuantilesSchema = z.object({ q10: IntSchema, q50: IntSchema, q90: IntSchema });
export const ModelMethodSchema = z.object({
  id: z.string(),
  version: z.string(),
  short_description: z.string(),
  methodology_url: z.string(),
  params_hash: z.string(),
});

export const ProbabilityPayloadSchema = z.object({
  status: ProbabilityStatusSchema,
  public_reason: z.nullable(z.string()),
  base_seq: IntSchema,
  generated_at: IsoSchema,
  method: z.object({ id: z.string(), version: z.string(), methodology_url: z.string() }),
  win_prob: z.nullable(z.record(CandidateNumberSchema, z.number())),
  final_share: z.nullable(z.record(CandidateNumberSchema, QuantilesSchema)),
  final_votes: z.optional(z.nullable(z.record(CandidateNumberSchema, VoteQuantilesSchema))),
  band: z.nullable(ChanceBandSchema),
  favorite: z.nullable(CandidateNumberSchema),
  call: z.nullable(
    z.object({
      state: z.enum(['none', 'above_99']),
      candidate: z.optional(CandidateNumberSchema),
      approved_at: z.optional(IsoSchema),
    }),
  ),
});

export const PlaceProjectionSchema = z.object({
  win_prob: z.record(CandidateNumberSchema, z.number()),
  final_share: z.record(CandidateNumberSchema, QuantilesSchema),
  final_votes: z.optional(z.record(CandidateNumberSchema, VoteQuantilesSchema)),
  final_margin_pp: z.optional(QuantilesSchema),
  remaining_valid: z.optional(QuantilesSchema),
  histogram: z.optional(
    z.object({
      variable: z.literal('final_share_b_pp'),
      start: z.number(),
      step: z.number(),
      counts: z.array(IntSchema),
      below: IntSchema,
      above: IntSchema,
      total: IntSchema,
    }),
  ),
});

export const ProbabilityDetailSchema = z.object({
  ...feedHeaderShape('results.probability'),
  base_seq: IntSchema,
  generated_at: IsoSchema,
  status: ProbabilityStatusSchema,
  method: ModelMethodSchema,
  simulations: IntSchema,
  national: PlaceProjectionSchema,
  places: z.partialRecord(PlaceCodeSchema, PlaceProjectionSchema),
  regions: z.optional(z.partialRecord(RegionCodeSchema, PlaceProjectionSchema)),
  governor: z.optional(z.partialRecord(UfCodeSchema, PlaceProjectionSchema)),
  stress: z.optional(z.object({ win_prob: z.record(CandidateNumberSchema, z.number()) })),
  diagnostics: z.object({
    prior_weight: z.number(),
    places_with_data: IntSchema,
    sections_fraction: z.number(),
    tse_lag_s: z.number(),
    duration_ms: z.number(),
    composition: z.optional(z.enum(['none', 'order_1t', 'sections_list'])),
    regions_with_data: z.optional(IntSchema),
    units_with_data: z.optional(IntSchema),
    stress_spec: z.optional(z.string()),
  }),
});
